// src/features/dossiers/pages/CreateDossierPage.jsx
import React, { useState } from 'react';
import {
    Box, Typography, Stack, Button, Paper,
    Divider, alpha, CircularProgress, useTheme,
} from '@mui/material';
import {
    PersonOutlined, GroupOutlined, CardTravelOutlined,
    CheckCircleOutline, ArrowForward, ArrowBack,
    CheckCircle, SaveOutlined,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { StepClient } from '../components/add-order-form/StepClient';
import { StepVoyageurs } from '../components/add-order-form/StepVoyageurs';
import { StepPrestations } from '../components/add-order-form/StepPrestations';

import apiClient from '../../../services/api-client';
import { useAuthStore } from '../../../store/useAuthStore';
import { useNotificationStore } from '../../../store/useNotificationStore';
import { useLocation } from 'react-router-dom';

// ── Étapes ────────────────────────────────────────────────────────────────────
const STEPS = [
    { key: 'client', label: 'Client', sub: 'Sélection ou création', icon: PersonOutlined },
    { key: 'voyageurs', label: 'Voyageurs', sub: 'Passagers du dossier', icon: GroupOutlined },
    { key: 'prestations', label: 'Prestations', sub: 'Services & tarifs', icon: CardTravelOutlined },
];

// ── Validation par étape ──────────────────────────────────────────────────────
function validateStep(step, data) {
    if (step === 0) return !!data.clientId;
    if (step === 1) return data.voyageurs?.length > 0 && data.voyageurs.every(v => v.nom && v.prenom && v.dateNaissance && v.numeroPasseport);
    if (step === 2) return data.prestations?.length > 0 && data.prestations.every(p => p.prixVente > 0);
    return true;
}

// ── Indicateur d'étape ────────────────────────────────────────────────────────
function StepIndicator({ step, current, total }) {
    const { icon: Icon, label, sub } = step;
    const idx = STEPS.indexOf(step);
    const done = idx < current;
    const active = idx === current;

    return (
        <Stack direction="row" spacing={2} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
            {/* Cercle numéro */}
            <Box
                sx={{
                    width: 40, height: 40, borderRadius: '12px', flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'all 0.3s ease',
                    bgcolor: done ? '#1a1a2e' : active ? '#1a1a2e' : 'rgba(26,26,46,0.06)',
                    border: active ? '2px solid #c9a84c' : 'none',
                    boxShadow: active ? '0 0 0 4px rgba(201,168,76,0.12)' : 'none',
                }}
            >
                {done
                    ? <CheckCircle sx={{ fontSize: 18, color: '#c9a84c' }} />
                    : <Icon sx={{ fontSize: 18, color: active ? '#c9a84c' : 'rgba(26,26,46,0.3)' }} />
                }
            </Box>
            {/* Texte */}
            <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: '0.82rem', fontWeight: active || done ? 700 : 500, color: active || done ? '#1a1a2e' : 'rgba(26,26,46,0.4)', lineHeight: 1.2 }}>
                    {label}
                </Typography>
                <Typography sx={{ fontSize: '0.68rem', color: 'rgba(26,26,46,0.4)' }}>{sub}</Typography>
            </Box>
        </Stack>
    );
}

// ── Résumé latéral ────────────────────────────────────────────────────────────
function SideRecap({ data, currentStep }) {
    const total = (data.prestations || []).reduce((s, p) => s + (Number(p.prixVente) || 0), 0);
    const hasClient = data.clientId;
    const voyCount = data.voyageurs?.length ?? 0;
    const prestCount = data.prestations?.length ?? 0;

    return (
        <Box sx={{ position: 'sticky', top: 0 }}>
            <Typography sx={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '1.1rem', fontWeight: 700, color: '#1a1a2e', mb: 2 }}>
                Récapitulatif
            </Typography>

            <Stack spacing={1.5}>
                {/* Client */}
                <Box sx={{ p: 2, borderRadius: '14px', bgcolor: hasClient ? 'rgba(201,168,76,0.06)' : 'rgba(26,26,46,0.03)', border: '1px solid', borderColor: hasClient ? 'rgba(201,168,76,0.25)' : 'rgba(26,26,46,0.08)' }}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                        <PersonOutlined sx={{ fontSize: 16, color: hasClient ? '#c9a84c' : 'rgba(26,26,46,0.3)' }} />
                        <Box>
                            <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: 'rgba(26,26,46,0.4)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>Client</Typography>
                            <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#1a1a2e' }}>
                                {data.clientInfo?.nomComplet || '—'}
                            </Typography>
                        </Box>
                    </Stack>
                </Box>

                {/* Voyageurs */}
                <Box sx={{ p: 2, borderRadius: '14px', bgcolor: voyCount > 0 ? 'rgba(201,168,76,0.06)' : 'rgba(26,26,46,0.03)', border: '1px solid', borderColor: voyCount > 0 ? 'rgba(201,168,76,0.25)' : 'rgba(26,26,46,0.08)' }}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                        <GroupOutlined sx={{ fontSize: 16, color: voyCount > 0 ? '#c9a84c' : 'rgba(26,26,46,0.3)' }} />
                        <Box>
                            <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: 'rgba(26,26,46,0.4)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>Voyageurs</Typography>
                            <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#1a1a2e' }}>
                                {voyCount > 0 ? `${voyCount} passager${voyCount > 1 ? 's' : ''}` : '—'}
                            </Typography>
                        </Box>
                    </Stack>
                    {voyCount > 0 && (
                        <Stack spacing={0.5} sx={{ mt: 1.5 }}>
                            {data.voyageurs.map(v => (
                                <Typography key={v.tempId} sx={{ fontSize: '0.72rem', color: 'rgba(26,26,46,0.55)', pl: 0.5 }}>
                                    · {v.prenom} {v.nom}
                                </Typography>
                            ))}
                        </Stack>
                    )}
                </Box>

                {/* Prestations */}
                <Box sx={{ p: 2, borderRadius: '14px', bgcolor: prestCount > 0 ? 'rgba(201,168,76,0.06)' : 'rgba(26,26,46,0.03)', border: '1px solid', borderColor: prestCount > 0 ? 'rgba(201,168,76,0.25)' : 'rgba(26,26,46,0.08)' }}>
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: prestCount > 0 ? 1.5 : 0 }}>
                        <CardTravelOutlined sx={{ fontSize: 16, color: prestCount > 0 ? '#c9a84c' : 'rgba(26,26,46,0.3)' }} />
                        <Box>
                            <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: 'rgba(26,26,46,0.4)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>Prestations</Typography>
                            <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#1a1a2e' }}>
                                {prestCount > 0 ? `${prestCount} service${prestCount > 1 ? 's' : ''}` : '—'}
                            </Typography>
                        </Box>
                    </Stack>
                    {prestCount > 0 && data.prestations.map(p => {
                        const TYPE_LABEL = { BILLET: 'Vol', RESERVATION_HOTEL: 'Hôtel', ASSURANCE: 'Assurance' };
                        return (
                            <Box key={p._id} sx={{ display: "flex", justifyContent: "space-between", sx: { pl: 0.5 } }}>
                                <Typography sx={{ fontSize: '0.72rem', color: 'rgba(26,26,46,0.55)' }}>· {TYPE_LABEL[p.type]}</Typography>
                                <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#1a1a2e' }}>
                                    {p.prixVente ? `${Number(p.prixVente).toLocaleString()} FCFA` : '—'}
                                </Typography>
                            </Box>
                        );
                    })}
                </Box>

                {/* Total */}
                {total > 0 && (
                    <Box sx={{ p: 2.5, borderRadius: '14px', background: 'linear-gradient(135deg, #c9a84c 0%, #e2c46e 100%)' }}>
                        <Typography sx={{ fontSize: '0.65rem', fontWeight: 800, color: 'rgba(26,26,46,0.55)', letterSpacing: '0.08em', textTransform: 'uppercase', mb: 0.3 }}>Total en FCFA</Typography>
                        <Typography sx={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '1.8rem', fontWeight: 700, color: '#1a1a2e', lineHeight: 1 }}>
                            {total.toLocaleString()}
                        </Typography>
                    </Box>
                )}
            </Stack>
        </Box>
    );
}

// ── Fonction de construction du payload final ─────────────────────────────────
// ── Construction du payload final pour POST /api/orders/with-client ──────────
//
// {
//   newClientDTO?     : { nomComplet, email, telephone, adresseFacturation, agentUsername }
//   existingClientId? : uuid   ← mutuellement exclusif avec newClientDTO
//   dossierVoyage: {
//     clientId?   : uuid   ← null si nouveau client (backend l'assignera)
//     voyageurs   : [{ tempId, nom, prenom, dateNaissance, numeroPasseport, dateExpirationPasseport }]
//     prestations : [{ type, prixVente, coutAchat, beneficiairesTempIds[], ...champs spécifiques }]
//   }
// }
//
// Points clés :
//   - Les voyageurs CONSERVENT leur tempId (le backend s'en sert pour résoudre beneficiairesTempIds)
//   - beneficiairesTempIds = tableau de strings (tempId), PAS d'objets
//   - dossierVoyage.clientId = uuid du client existant, null si nouveau
// ─────────────────────────────────────────────────────────────────────────────

function buildPayload(data, agentUsername = '') {
    const isNew = data.clientId === '__new__';

    // Voyageurs : on conserve le tempId — le backend en a besoin pour résoudre les bénéficiaires
    const voyageurs = (data.voyageurs || []).map(v => ({
        tempId: v.tempId,
        nom: v.nom,
        prenom: v.prenom,
        dateNaissance: v.dateNaissance,
        numeroPasseport: v.numeroPasseport,
        dateExpirationPasseport: v.dateExpirationPasseport || null,
    }));

    // Construction d'une prestation selon son type
    const buildPrestation = (p) => {
        // Champs communs
        // ✅ Fallback : si aucun bénéficiaire sélectionné manuellement,
        //    on assigne automatiquement tous les voyageurs du dossier
        const allTempIds = voyageurs.map(v => v.tempId);
        const resolvedBeneficiaires = (p.beneficiairesTempIds || []).length > 0
            ? p.beneficiairesTempIds
            : allTempIds;

        const base = {
            type: p.type,
            prixVente: Number(p.prixVente),
            coutAchat: Number(p.coutAchat) || 0,
            beneficiairesTempIds: resolvedBeneficiaires,
        };

        if (p.type === 'BILLET') {
            return {
                ...base,
                pnr: p.pnr || null,
                numeroBillet: p.numeroBillet || null,
                segments: (p.segments || []).map(({ _id, ...seg }) => ({
                    numeroVol: seg.numeroVol || null,
                    compagnieAerienne: seg.compagnieAerienne || null,
                    codeAeroportDepart: seg.codeAeroportDepart || null,
                    codeAeroportArrivee: seg.codeAeroportArrivee || null,
                    // datetime-local ("2026-04-19T08:30") → ISO 8601 avec Z
                    dateHeureDepart: seg.dateHeureDepart ? new Date(seg.dateHeureDepart).toISOString() : null,
                    dateHeureArrivee: seg.dateHeureArrivee ? new Date(seg.dateHeureArrivee).toISOString() : null,
                    classeReservation: seg.classeReservation || null,
                    franchiseBagage: seg.franchiseBagage || null,
                })),
            };
        }

        if (p.type === 'RESERVATION_HOTEL') {
            return {
                ...base,
                nomHotel: p.nomHotel || null,
                ville: p.ville || null,
                adresseComplete: p.adresseComplete || null,
                numeroConfirmationFournisseur: p.numeroConfirmationFournisseur || null,
                dateCheckIn: p.dateCheckIn || null,
                dateCheckOut: p.dateCheckOut || null,
                typeChambre: p.typeChambre || null,
                typePension: p.typePension || null,
            };
        }

        if (p.type === 'ASSURANCE') {
            return {
                ...base,
                compagnieAssurance: p.compagnieAssurance || null,
                numeroPolice: p.numeroPolice || null,
                typeCouverture: p.typeCouverture || null,
                zoneGeographique: p.zoneGeographique || null,
                dateDebutCouverture: p.dateDebutCouverture || null,
                dateFinCouverture: p.dateFinCouverture || null,
            };
        }

        return base;
    };

    // ── Payload racine ────────────────────────────────────────────────────────
    const payload = {
        dossierVoyage: {
            // null si nouveau client — le backend utilisera newClientDTO pour créer le client
            // et associer son id au dossier
            clientId: isNew ? null : data.clientId,
            voyageurs,
            prestations: (data.prestations || []).map(buildPrestation),
        },
    };

    // Client : nouveau OU existant (mutuellement exclusifs)
    if (isNew) {
        payload.newClientDTO = {
            nomComplet: data.clientInfo?.nomComplet || '',
            email: data.clientInfo?.email || '',
            telephone: data.clientInfo?.telephone || '',
            adresseFacturation: data.clientInfo?.adresseFacturation || '',
            agentUsername,
        };
    } else {
        payload.existingClientId = data.clientId;
    }

    return payload;
}

const INITIAL_FORM_DATA = {
    clientId: null,
    voyageurs: [],
    prestations: [],
    newClientDTO: {
        nomComplet: '',
        email: '',
        telephone: '',
        adresseFacturation: '',
        agentUsername: ''
    }
};

// ── Page principale ───────────────────────────────────────────────────────────
export function CreateDossierPage({ onClose = null }) {
    const navigate = useNavigate();
    const [currentStep, setCurrentStep] = useState(0);
    const [submitting, setSubmitting] = useState(false);
    const location = useLocation();

    // Récupération de l'agent connecté depuis Zustand
    const user = useAuthStore((state) => state.user);
    const showNotify = useNotificationStore(state => state.show);

    const [formData, setFormData] = useState(INITIAL_FORM_DATA);

    // const [formData, setFormData] = useState({
    //     clientId: null, clientInfo: null,
    //     voyageurs: [],
    //     prestations: [],
    // });

    const mergeData = (partial) => setFormData(prev => ({ ...prev, ...partial }));

    const canNext = validateStep(currentStep, formData);
    const isLast = currentStep === STEPS.length - 1;

    const handleNext = () => {
        if (!isLast) setCurrentStep(s => s + 1);
    };

    // Fonction de nettoyage
    const resetForm = () => {
        setFormData(INITIAL_FORM_DATA);
        setCurrentStep(0);
    };

    const handleSubmit = async () => {
        setSubmitting(true);
        try {
            // Utilisation du nom d'utilisateur réel de l'agent
            const agentUsername = user?.username || 'system';

            const payload = buildPayload(formData, agentUsername);

            // On utilise apiClient (Axios) pour profiter des intercepteurs de tokens
            await apiClient.post('/api/orders/with-client', payload);

            resetForm();
            showNotify({
                type: 'success',
                title: 'Dossier Enregistré',
                message: 'Le dossier a été transmis avec succès à la chef d’agence.',
                onConfirm: () => resetForm()
            });

        } catch (error) {
            showNotify({
                type: 'error',
                message: error.response?.data?.message || 'Erreur technique'
            });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Box sx={{ width: '100%' }}>
            {/* ── Titre ── */}
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 4 }}>
                <Box>
                    <Typography sx={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '2rem', fontWeight: 700, color: '#1a1a2e', lineHeight: 1.1, mb: 0.4 }}>
                        Nouveau dossier
                    </Typography>
                    <Typography sx={{ fontSize: '0.82rem', color: 'rgba(26,26,46,0.45)', fontFamily: "'DM Sans', sans-serif" }}>
                        Étape {currentStep + 1} sur {STEPS.length} · {STEPS[currentStep].label}
                    </Typography>
                </Box>
                {onClose !== null ? (
                    <Button onClick={onClose} sx={{ color: 'rgba(26,26,46,0.45)', fontFamily: "'DM Sans', sans-serif", textTransform: 'none', fontSize: '0.82rem', '&:hover': { color: '#1a1a2e' } }}>
                        Fermer
                    </Button>
                ) : (
                    <Button onClick={() => navigate(-1)} sx={{ color: 'rgba(26,26,46,0.45)', fontFamily: "'DM Sans', sans-serif", textTransform: 'none', fontSize: '0.82rem', '&:hover': { color: '#1a1a2e' } }}>
                        Annuler
                    </Button>
                )}
            </Box>

            {/* ── Stepper visuel ── */}
            <Box sx={{ mb: 4, p: 2.5, borderRadius: '18px', bgcolor: 'white', border: '1px solid rgba(26,26,46,0.07)', boxShadow: '0 2px 12px rgba(26,26,46,0.04)' }}>
                <Stack direction="row" spacing={0} alignItems="center">
                    {STEPS.map((step, idx) => (
                        <React.Fragment key={step.key}>
                            <StepIndicator step={step} current={currentStep} total={STEPS.length} />
                            {idx < STEPS.length - 1 && (
                                <Box sx={{ width: 40, height: 1, bgcolor: idx < currentStep ? '#c9a84c' : 'rgba(26,26,46,0.1)', mx: 1, flexShrink: 0, transition: 'background-color 0.3s' }} />
                            )}
                        </React.Fragment>
                    ))}
                </Stack>
            </Box>

            {/* ── Corps principal : formulaire + récap ── */}
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 280px', gap: 3, alignItems: 'start' }}>

                {/* Formulaire de l'étape active */}
                <Box sx={{ p: 4, borderRadius: '20px', bgcolor: 'white', border: '1px solid rgba(26,26,46,0.07)', boxShadow: '0 2px 12px rgba(26,26,46,0.04)', minHeight: 400 }}>
                    {currentStep === 0 && <StepClient data={formData} onChange={mergeData} />}
                    {currentStep === 1 && <StepVoyageurs data={formData} onChange={mergeData} />}
                    {currentStep === 2 && <StepPrestations data={formData} onChange={mergeData} />}

                    {/* Navigation */}
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 4, pt: 3, borderTop: '1px solid rgba(26,26,46,0.07)' }}>
                        <Button
                            onClick={() => setCurrentStep(s => s - 1)}
                            disabled={currentStep === 0}
                            startIcon={<ArrowBack sx={{ fontSize: '16px !important' }} />}
                            sx={{
                                px: 2.5, py: 1.2, borderRadius: '12px',
                                color: 'rgba(26,26,46,0.5)', fontFamily: "'DM Sans', sans-serif",
                                fontWeight: 600, fontSize: '0.85rem', textTransform: 'none',
                                border: '1px solid rgba(26,26,46,0.12)',
                                '&:hover': { bgcolor: 'rgba(26,26,46,0.04)', color: '#1a1a2e' },
                                '&.Mui-disabled': { opacity: 0.35 },
                            }}
                        >
                            Retour
                        </Button>

                        {!isLast ? (
                            <Button
                                onClick={handleNext}
                                disabled={!canNext}
                                endIcon={<ArrowForward sx={{ fontSize: '16px !important' }} />}
                                sx={{
                                    px: 3, py: 1.2, borderRadius: '12px',
                                    bgcolor: canNext ? '#1a1a2e' : 'rgba(26,26,46,0.08)',
                                    color: canNext ? 'white' : 'rgba(26,26,46,0.3)',
                                    fontFamily: "'DM Sans', sans-serif", fontWeight: 700, fontSize: '0.85rem',
                                    textTransform: 'none', transition: 'all 0.2s',
                                    boxShadow: canNext ? '0 4px 16px rgba(26,26,46,0.2)' : 'none',
                                    position: 'relative', overflow: 'hidden',
                                    '&::after': { content: '""', position: 'absolute', inset: 0, background: 'linear-gradient(135deg, rgba(201,168,76,0.2) 0%, transparent 60%)', opacity: 0, transition: 'opacity 0.2s' },
                                    '&:hover': { bgcolor: canNext ? '#1a1a2e' : 'rgba(26,26,46,0.08)', '&::after': { opacity: canNext ? 1 : 0 }, transform: canNext ? 'translateY(-1px)' : 'none' },
                                    '&.Mui-disabled': { bgcolor: 'rgba(26,26,46,0.06)', color: 'rgba(26,26,46,0.25)' },
                                }}
                            >
                                Continuer
                            </Button>
                        ) : (
                            <Button
                                onClick={handleSubmit}
                                disabled={!canNext || submitting}
                                startIcon={submitting
                                    ? <CircularProgress size={16} sx={{ color: 'rgba(26,26,46,0.5)' }} />
                                    : <SaveOutlined sx={{ fontSize: '17px !important' }} />
                                }
                                sx={{
                                    px: 3.5, py: 1.2, borderRadius: '12px',
                                    background: canNext && !submitting ? 'linear-gradient(135deg, #c9a84c 0%, #e2c46e 100%)' : 'rgba(26,26,46,0.08)',
                                    color: canNext && !submitting ? '#1a1a2e' : 'rgba(26,26,46,0.3)',
                                    fontFamily: "'DM Sans', sans-serif", fontWeight: 800, fontSize: '0.85rem',
                                    textTransform: 'none', transition: 'all 0.2s',
                                    boxShadow: canNext ? '0 4px 16px rgba(201,168,76,0.3)' : 'none',
                                    '&:hover': { transform: canNext ? 'translateY(-1px)' : 'none', boxShadow: canNext ? '0 6px 24px rgba(201,168,76,0.4)' : 'none' },
                                    '&.Mui-disabled': { background: 'rgba(26,26,46,0.06)', color: 'rgba(26,26,46,0.25)' },
                                }}
                            >
                                {submitting ? 'Enregistrement...' : 'Enregistrer le dossier'}
                            </Button>
                        )}
                    </Stack>
                </Box>

                {/* Récap latéral */}
                <SideRecap data={formData} currentStep={currentStep} />
            </Box>
        </Box>
    );
}